import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { addDays, isSameDay } from 'date-fns';
import { parseAvailability } from '@fshr/shared';
import type { Value } from '@fshr/shared/types';
import Button from '../../../button';
import Calendar from '../../../calendar';
import CancellationPolicyModal from '../../../cancellation-policy-modal';
import type { TileArgs } from '../../../calendar';

import type { AvailableDateRange, Availability, DateRange } from './types';

export type DateSelectionProps = {
	locale: string;
	strictRangeSelect: boolean;
	selectedRange: DateRange;
	availableRange?: AvailableDateRange;
	availability?: Availability[];
	callout?: string;
	qualifier?: string;
	cancellationPolicy?: string;
	t: (text: string, params?: (string | number)[]) => string;
	onApply: (range: DateRange) => void;
	allowPartialRange: boolean;
};

/**
 * Matches the Figma "Choose Your Dates" calendar. Ported from `DatesSelect.tsx`, minus the mobile
 * dialog/desktop-inline split - this widget stays inline everywhere. Uses the existing `Calendar`
 * (`libs/components/src/lib/calendar`) for the grid/navigation - it already renders the range as a
 * filled pill and strikes through unavailable dates, matching the Figma spec, rather than the
 * hand-rolled grid this file used to have. `parseAvailability` (also already in `@fshr/shared`) turns
 * `product.calendar.availability`'s per-day status codes into the `unavailableDates`/`restrictedDates`
 * arrays `Calendar` wants.
 */
export default function DateSelection({
	locale,
	strictRangeSelect,
	selectedRange,
	availableRange,
	availability,
	callout,
	qualifier,
	cancellationPolicy,
	t,
	onApply,
	allowPartialRange,
}: DateSelectionProps): ReactElement {
	const { unavailable, restricted } = useMemo(
		() => parseAvailability(availability ?? []),
		[availability]
	);

	// AC 2.1: pre-select the nearest available date so the calendar opens with
	// something already highlighted rather than fully empty.
	const firstAvailableDate = useMemo(() => {
		for (let i = 0; i < 366; i++) {
			const candidate = addDays(new Date(), i);
			if (!unavailable.some(u => isSameDay(u, candidate))) {
				return candidate;
			}
		}
		return new Date();
	}, [unavailable]);

	const defaultValue: [Date, Date] =
		selectedRange.start && selectedRange.end
			? [selectedRange.start, selectedRange.end]
			: [firstAvailableDate, firstAvailableDate];

	const [draftRange, setDraftRange] = useState<DateRange>(() =>
		selectedRange.start || selectedRange.end
			? selectedRange
			: { start: firstAvailableDate, end: firstAvailableDate }
	);

	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

	// AC 4.1: detect narrow viewport so Calendar stacks months vertically (mobile
	// layout) instead of showing single-month + navigation arrows.
	const [isNarrow, setIsNarrow] = useState(false);
	useEffect(() => {
		const mq = window.matchMedia('(max-width: 699px)');
		setIsNarrow(mq.matches);
		const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, []);

	const handleChange = (value: Value) => {
		if (Array.isArray(value)) {
			if (value[0] && value[1]) {
				setDraftRange({ start: value[0], end: value[1] });
			} else if (value[0]) {
				setDraftRange({ start: value[0], end: value[0] });
			}
		} else if (value) {
			// Normalize a single Date into a same-day range. The Calendar always runs in
			// range mode, so this is a defensive fallback that keeps "Check Availability"
			// with a complete start+end pair even if a plain Date ever surfaces.
			setDraftRange({ start: value, end: value });
		}
	};

	// Sold-out dates are struck through by `Calendar` automatically via `unavailableDates`, but that's
	// display-only - block them from actually being pickable too. Restricted ("R"/limited) dates stay
	// selectable, same as the legacy widget.
	const isTileDisabled = useCallback(
		({ date }: TileArgs) => unavailable.some(d => isSameDay(d, date)),
		[unavailable]
	);

	return (
		<div className="flex flex-col" data-testid="date-selection">
			<Calendar
				locale={locale}
				selectRange
				strictRangeSelect={strictRangeSelect}
				defaultValue={defaultValue}
				minDate={availableRange?.start}
				maxDate={availableRange?.end}
				unavailableDates={unavailable}
				restrictedDates={restricted}
				tileDisabled={isTileDisabled}
				onChange={handleChange}
				showFooter={false}
				allowPartialRange
				// AC 4.1 / AC 2.1 mobile: on narrow viewports use the stacked-months mobile
				// layout (no nav arrows, months scroll vertically, next month is visible below).
				isMobile={isNarrow}
				showNavigation={!isNarrow}
				showMobileHeader={false}
				showFixedNumberOfWeeks={false}
				className="!px-300 !py-300"
			/>

			<div className="flex flex-col gap-150 px-300 py-200 border-t border-border-subtle">
				<div className="flex items-start justify-between">
					<div className="flex min-w-0 flex-wrap items-baseline spacing-inline-2xs">
						{callout && <span className="text-subtitle2 text-text-heading">{callout}</span>}
						{qualifier && <span className="text-caption text-text-primary">{qualifier}</span>}
					</div>

					<button
						type="button"
						className="text-caption text-button-primary underline w-fit shrink-0 cursor-pointer hover:text-button-primary-hover transition-colors"
						onClick={() => setIsCancelModalOpen(true)}
					>
						{t('Cancellation Policy')}
					</button>
				</div>
				<Button
					intent="filled"
					className="w-full"
					onClick={() => onApply(draftRange)}
					// disabled={!draftRange.start || !draftRange.end}
					disabled={!draftRange.end}
				>
					{t('Check Availability')}
				</Button>
			</div>
			<CancellationPolicyModal
				isOpen={isCancelModalOpen}
				onClose={() => setIsCancelModalOpen(false)}
				title={t('Cancellation Policy')}
				cancellationPolicy={cancellationPolicy ?? ''}
			/>
		</div>
	);
}
